import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type maplibregl from 'maplibre-gl'
import { cabinetApi } from '../api'
import type { BBox, NavwarnCollection } from '../api'
import { presentKinds } from './navwarn'

/** Минимум, который хук просит у карты — как у промрайонов. */
export interface NavwarnMapSink {
  getMap: () => maplibregl.Map | null
  getBBox: () => BBox
  setNavwarn: (data: GeoJSON.FeatureCollection) => void
  removeNavwarn: () => void
}

export interface NavwarnMeta {
  /** Роды, легшие в кадр, — легенда называет только их. */
  kinds: string[]
  disclaimerKey: string | null
  attribution: string | null
  /** Ответ целиком — раздел «Предупреждения» строит по нему список. */
  data: NavwarnCollection | null
}

const OFF: NavwarnMeta = { kinds: [], disclaimerKey: null, attribution: null, data: null }
const DEBOUNCE_MS = 250

/**
 * Навигационные предупреждения NGA в силе на дату карты.
 *
 * Запрос идёт заново на смене кадра И даты: предупреждение либо в силе в этот
 * день, либо нет — как запрет промысла.
 */
export function useNavwarnLayer(
  mapRef: RefObject<NavwarnMapSink | null>,
  enabled: boolean,
  viewKey: string,
  date: string | null,
): NavwarnMeta {
  const [meta, setMeta] = useState<NavwarnMeta>(OFF)

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled) {
      map.removeNavwarn()
      setMeta(OFF)
      return
    }
    let alive = true
    const id = setTimeout(() => {
      cabinetApi
        .navwarn({ bbox: map.getBBox(), date })
        .then(fc => {
          if (!alive) return
          if (fc.features?.length) map.setNavwarn(fc as unknown as GeoJSON.FeatureCollection)
          else map.removeNavwarn()
          setMeta({
            kinds: presentKinds(fc.features.map(f => f.properties.kind)),
            disclaimerKey: fc.meta?.disclaimer_key ?? null,
            attribution: fc.meta?.attribution ?? null,
            data: fc,
          })
        })
        .catch(() => {
          if (!alive) return
          map.removeNavwarn()
          setMeta(OFF)
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewKey, date])

  useEffect(
    () => () => mapRef.current?.removeNavwarn(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return meta
}
